import Vue from 'vue';
import Vuex from 'vuex';
import assign from 'latest_modules/lodash/assign';
import textUtil from '../../../util/text';
import urlUtil from '../../../util/url';
import bigdecimalUtil from '../../../../../../../lib/util/bigdecimal';
import pointsRedemptionApi from '../../../api/pointsRedemption';

const {mapState} = Vuex;

import ProductSku from '../../../stores/schema/ProductSku';

import priceEmphasisComponent from '../priceEmphasis.vue';
import stockLabelComponent from './stockLabel';
import shippingInfoComponent from './shippingInfo';
import reviewItemsComponent from './reviewItems';
import mobilePayComponent from './mobilePay.vue';
import signupFormComponent from '../../../components/signup/signupForm';
import loginFormComponent from '../../../components/login/loginForm';
import duration from 'dayjs/plugin/duration';
import dayjs from 'dayjs';

dayjs.extend(duration);

const MAIN_IMAGE_WIDTH = 450;
const THUMB_IMAGE_WIDTH = 70;

const REGION_LOCATION = {
  Maihama: 'Japan',
  PDX: 'United States',
  Commerce: 'United States'
};

window.vueEventBus = window.vueEventBus || {};

Vue.use(Vuex);

const statuses = {
  LOADING: 'LOADING',
  LOADED: 'LOADED'
};

const progressBaseColor = '#e0e0e0';
const progressActiveColor = '#e30920';

/*
Productsの詳細を表示するコンポーネント

- product 情報の読み込み
- カルーセルの表示
- Skuの選択
- 価格/ポイント表示
- 国制限の表示
- 在庫表示
- カートへの追加
- restock notificationへの追加/削除
- wishlistへの追加/削除
- Shareボタンの表示
- Premium入会案内の表示
- Premium入会チェックボックスの選択
- SKU ラベルのフォロー/フォロー解除

を行う。


```
vue-product(:product-id='product._id')
```

としてProductを指定する。

template はここでは定義していない。以下のいずれかで定義する。
- inline-templateを使う
- 継承した.vueファイルを作成する
- scriptタグでテンプレートを定義する

```
<script id="vue-product-template" type="text/x-template">...</script>
```
```
new Vue
  components:
    'vue-product': _.extend productComponent,
      template: '#vue-product-template'
```

@see https://jp.vuejs.org/v2/guide/components.html#インラインテンプレート
*/

export default {
  mixins: [window.detailsVueMixin || {}],

  components: {
    'vue-price-emphasis': priceEmphasisComponent,
    'vue-stock-label': stockLabelComponent,
    'vue-shipping-info': shippingInfoComponent,
    'vue-review-items': reviewItemsComponent,
    'vue-mobile-pay-button': mobilePayComponent,
    'vue-signup-form': signupFormComponent,
    'vue-login-form': loginFormComponent
  },

  props: ['productId', 'isDesktop', 'closingAt', 'useMagnifier'],

  data() {
    return {
      status: statuses.LOADING,
      isInitialSKUSelection: true,
      state: {
        isOpeningSkuSelector: false
      },
      restockNotification: {
        email: '',
        loading: false,
        errors: null
      },

      wishList: {
        changedNow: false,
        baseCount: 0
      },

      landingState: {
        // このページにランディング時の状態を保存
        willJoinPremium: false
      },

      modalTOMPremiumSummary: null,

      selectedSkuImage: null,
      selectedSkuThumb: null,
      selectedSkuCaption: null,

      loadedProductData: false,

      closingCountDownTimer: null,

      sampleAudioTrack: null
    };
  },

  computed: assign(
    {
      isLoading() {
        return this.status === statuses.LOADING;
      },

      isLoaded() {
        return this.status === statuses.LOADED;
      },

      // Sales Method map
      SALES_METHOD() {
        return ProductSku.SALES_METHOD;
      },

      // helpers
      allowedCountrySkus() {
        return this.$store.getters['product/skus/allowedCountrySkus'];
      },

      selectedSku() {
        return this.$store.getters['product/skus/selectedSku'];
      },

      // Earn points
      // Mixin に渡すための変数として使用
      acquirePoint() {
        return this.currency(this.getAcquirePoint(this.selectedSku, this.user));
      },
      hasPercentage() {
        return this.selectedSku != null;
      },
      percentage() {
        return this.getPointPercentage(this.selectedSku, this.user);
      },
      hasMultiplePointRates() {
        return false;
      },
      pointClass() {
        return this.getPointClass(this.selectedSku);
      },
      showPriceArea() {
        return (
          this.user.is_premium_member ||
          !this.selectedSku.has_premium_price ||
          this.selectedSku.is_out_of_stock ||
          this.selectedSku.is_points_redemption
        );
      },
      hasLabels() {
        return (
          this.selectedSku.sales_method === this.SALES_METHOD.BTO ||
          this.selectedSku.sales_method === this.SALES_METHOD.PRE_ORDER ||
          this.isFreeShipping(this.selectedSku) ||
          this.isFreeShippingForNewCustomer(this.selectedSku)
        );
      },

      // Wish list のラベル
      addToWishListButtonLabel() {
        if (this.selectedSku.is_added_to_wishlist) {
          return 'Added to Favorites';
        }
        return 'Add to Favorites';
      },

      // Wish list に追加されたcountのラベル
      addedWishListCountLabel() {
        let cnt = this.selectedSku.wishlist_items_count;
        // このページで変更が行われていたら
        if (this.wishList.changedNow) {
          cnt = this.wishList.baseCount;
          if (this.selectedSku.is_added_to_wishlist) {
            cnt += 1;
          }
        }
        if (cnt < 1) {
          return '';
        } else {
          return `${cnt}`;
        }
      },

      warehouseLocation() {
        if (this.selectedSku && this.selectedSku.region) {
          return REGION_LOCATION[this.selectedSku.region];
        }
        return '';
      },

      isWaitingAutoRecurringPremium() {
        return (
          this.user.will_join_premium &&
          this.user.has_waiting_auto_recurring_premium
        );
      },

      dayjs() {
        return dayjs;
      },

      sampleAudioSeek() {
        if (!this.$store.state.audioPlayer.loaded || !this.$store.state.audioPlayer.playing) {
          return 0;
        }
        return this.$store.getters['audioPlayer/seek'];
      },

      sampleAudioProgress: {
        get() {
          if (this.$store.getters['audioPlayer/playingContent'] && !this.$store.state.audioPlayer.playing) {
            return (
              (this.sampleAudioSeek /
                this.$store.getters['audioPlayer/playingContent'].play_time) * 100 || 0
            );
          }
          return (this.sampleAudioSeek / this.$store.getters['audioPlayer/duration']) * 100 || 0;
        },
        set(val) {
          if (!this.$store.getters['audioPlayer/loaded']) {
            return;
          }
          this.$store.dispatch('audioPlayer/seek', {per: val / 100});
        }
      },

      isPlaying() {
        return this.$store.state.audioPlayer.playing;
      }
    },
    mapState('user/', {
      user(state) {
        return state;
      }
    }),
    mapState('product/', {
      product(state) {
        return state;
      }
    }),
    {
      isNewCustomer() {
        return this.user.is_new_customer;
      },

      addToCartFromPremumSummaryTrigger() {
        return this.$store.state.addToCartFromPremumSummaryTrigger;
      },

      lazyloadTrigger() {
        return this.$store.state.lazyloadTrigger;
      }
    }
  ),

  created() {
    window.addEventListener(
      'openSliderImage',
      (e) => {
        return this.onClickPic({
          target: e.detail
        });
      },
      false
    );

    // クリップボードコピー機能
    this.initializeClipboardCopy();

    // TOM Premium Modal
    if (!this.isDesktop) {
      this.initializeModalPremiumSummary();
    }

    if (this.$store.state.user.is_login) {
      // フォロー中のラベルを取得
      return this.$store.dispatch('labels/fetch');
    }
  },

  mounted() {
    if (window.productProvider.provider) {
      window.productProvider.setResolver((data) => {
        return this.$store.dispatch('product/setData', data).then(() => {
          // fetchData の then と同じ
          if (
            this.selectedSku.has_premium_price &&
            !this.user.can_buy_with_annual_plan
          ) {
            this.onToggleJoinPremium(null, true);
          }
          this.loadedProductData = true;
          return this.onFetchData && this.onFetchData();
        });
      });
    } else {
      this.fetchData(this.productId);
    }

    // ランディング時のPremium入会フラグの状態を保存
    this.landingState.willJoinPremium = this.user.will_join_premium;

    // デスクトップ版のメイン画像の拡大機能
    if (this.useMagnifier && window.Magnifier) {
      this.initializeMagnifier();
    }

    this.startClosingCountDown();

    return (this.status = statuses.LOADED);
  },

  watch: {
    addToCartFromPremumSummaryTrigger() {
      // event parameter は
      return this.onAddToCartFromTomPremiumSummary(true);
    },

    lazyloadTrigger() {
      return this.$nextTick(() => window.lozadObserver.observe());
    },

    // test [closingAt is falsy] が落ちるので、一旦モバイルのプログレスバーは諦める
    // sampleAudioProgress(per) {
    //   this.updateProgressSampleAudio(per);
    // }
  },

  methods: {
    // Closing Timeが指定されている場合はカウントダウンを表示する
    startClosingCountDown() {
      if (!this.closingAt) {
        return false;
      }

      const timerArray = [];

      const currentDate = dayjs();
      const closingAt = dayjs(this.closingAt);
      const diff = closingAt.diff(currentDate);

      // 指定された時間を超えればリロードする
      // OOSになって購入出来ないようになっているはず
      if (diff < 0) {
        window.location.reload();
        return false;
      }

      const duration = dayjs.duration(diff);

      timerArray.push(`${Math.floor(duration.days())} Days`);
      timerArray.push(`${duration.hours()} Hours`);
      timerArray.push(`${duration.minutes()} Minutes`);
      timerArray.push(`${duration.seconds()} Seconds`);

      this.closingCountDownTimer = timerArray.join(' : ');

      return window.requestAnimationFrame(this.startClosingCountDown);
    },

    // シェア用モーダル内のクリップボードコピー機能を有効にする
    initializeClipboardCopy() {
      // eslint-disable-next-line no-undef
      const clipboard = new Clipboard('.js-clipBtn');
      const showClipedTooltip = function (target, msg) {
        $(target)
          .attr('data-tooltip', msg)
          .addClass('c-tooltip c-tooltip--show');
        return setTimeout(
          () => $(target).removeClass('c-tooltip c-tooltip--show'),
          1500
        );
      };
      clipboard.on('success', (e) => showClipedTooltip(e.trigger, 'Copied!'));
      return clipboard.on('error', function (e) {
        let msg;
        if (/iPhone|iPad/i.test(window.navigator.userAgent)) {
          msg = 'No support :(';
        } else if (/Mac/i.test(window.navigator.userAgent)) {
          msg = 'Press ⌘-C to copy';
        } else {
          msg = 'Press Ctrl-C to copy';
        }
        return showClipedTooltip(e.trigger, msg);
      });
    },

    // TOM Premium Modal初期化
    initializeModalPremiumSummary() {
      return (this.modalTOMPremiumSummary = $(
        '[data-remodal-id="modal-summary-tom-premium"]'
      ).remodal({
        hashTracking: false
      }));
    },

    // 拡大画像表示
    initializeMagnifier() {
      return (this.magnifier = new window.Magnifier(new window.Event()));
    },

    setMagnifier(imageNum) {
      if (!this.useMagnifier || !window.Magnifier) {
        return;
      }
      const thumbClassName = '.js-magnifier-' + imageNum;
      const magnifierPreviewName = 'magnifierPreview';
      const $thumb = $(thumbClassName);
      if (!$thumb) {
        return;
      }
      const largeSizeUrl = $thumb.data('preview-image');
      const $preview = $('#' + magnifierPreviewName);
      if (!$preview) {
        return;
      }
      const largeWidth = $thumb.data('preview-width');
      const largeHeight = $thumb.data('preview-height');
      const previewWidth = $preview.width();
      $preview.css({
        height: (previewWidth * largeHeight) / largeWidth,
        opacity: 0,
        zIndex: -1
      });
      $thumb.removeClass('opaque');

      // 拡大画像がある場合
      if (largeSizeUrl !== '') {
        this.magnifier.attach({
          thumb: thumbClassName,
          large: largeSizeUrl,
          largeWrapper: magnifierPreviewName,
          zoomable: false,
          zoom: largeWidth / previewWidth,
          onthumbenter() {
            return $preview.css({
              zIndex: 5,
              opacity: 1
            });
          },
          onthumbmove() {
            return $preview.css({
              zIndex: 5,
              opacity: 1
            });
          },
          onthumbleave() {
            return $preview.css({
              zIndex: -10,
              opacity: 0
            });
          }
        });
        return this.magnifier.restart();
      } else {
        return this.magnifier.pause();
      }
    },

    trackGA(labelName) {
      if (tom && tom.helper && tom.helper.ga) {
        tom.helper.ga.push(['event', 'EC', 'Clicked', labelName, 1]);
      }
    },

    /*
    Actions
    */
    // 商品データを取得
    fetchData(productId) {
      return this.$store
        .dispatch('product/fetchOne', {
          _id: productId,
          is_cart_empty: this.user.is_cart_empty,
          is_login: this.user.is_login,
          premium_plan_welcome_point:
            this.user.possible_premium_plan != null
              ? this.user.possible_premium_plan.point
              : undefined,
          premium_plan_recontract_extra_point: this.user.recontract_extra_point,
          has_welcoming_gift_coupon: this.user.has_welcoming_gift_coupon
        })
        .then(() => {
          // 読み込み時のSKUが Premium 会員価格が設定されていれば、この state の入会フラグをオンにする（セッションは変更しない）
          if (
            this.selectedSku.has_premium_price &&
            !this.user.can_buy_with_annual_plan
          ) {
            this.onToggleJoinPremium(null, true);
          }

          return (this.loadedProductData = true);
        })
        .catch(function (err) {
          if (err.message) {
            return window.alert(err.message || 'An error occurred.');
          }
        })
        .then(() => {
          // detailsVueMixinで拡張されていた場合のみ実行する
          return this.onFetchData && this.onFetchData();
        });
    },

    // SKUを選択
    selectSku(e) {
      const skuId = e.target.value;

      // @changeだと現在選択されているSKUだと押下出来ないので、初回のみ同SKU IDでも押下出来るようにした
      if (this.selectedSku._id === skuId && !this.isInitialSKUSelection) {
        return false;
      }

      this.isInitialSKUSelection = false;

      return this.$store
        .dispatch('product/skus/select', {
          _id: skuId
        })
        .then(() => {
          if (this.selectedSku.image.source) {
            this.selectedSkuImage = urlUtil.webp(
              urlUtil.resizedImageUrl(
                this.selectedSku.image.source,
                MAIN_IMAGE_WIDTH
              )
            );
            this.selectedSkuThumb = urlUtil.webp(
              urlUtil.resizedImageUrl(
                this.selectedSku.image.source,
                THUMB_IMAGE_WIDTH
              )
            );
            this.selectedSkuCaption =
              (this.selectedSku.short_titles != null
                ? this.selectedSku.short_titles.en_us
                : undefined) || '';
          } else {
            this.selectedSkuImage = null;
            this.selectedSkuThumb = null;
            this.selectedSkuCaption = null;
          }
          this.$nextTick(() => {
            let alt;
            let src = (alt = undefined);
            let width, height;
            if (this.allowedCountrySkus.length > 1 && this.selectedSkuImage) {
              const url = urlUtil.resizedImageUrl(this.selectedSku.image);
              src = urlUtil.webp(url);
              alt = this.selectedSku.short_titles.en_us;
              width = this.selectedSku.image.width;
              height = this.selectedSku.image.height;
            }
            return this.$root.$emit('refresh-slider', {
              src,
              alt,
              width,
              height
            });
          });
          return this.onCloseSkuSelector();
        });
    },

    // Audio contents
    // サンプル再生
    async playSampleAudio() {
      if (!(this.selectedSku.audio_content && this.selectedSku.audio_content._id)) {
        return;
      }

      if (!this.$store.getters['audioPlayer/playingContent']) {
        try {
          await this.$store.dispatch('audioContents/fetchById', {
            _id: this.selectedSku.audio_content._id
          });
        } catch (e) {
          console.log('error fetchById', e);
          return;
        }

        await this.$store.dispatch('audioPlayer/setPlayId', {
          _id: this.selectedSku.audio_content._id
        });
        this.sampleAudioTrack = await this.$store.dispatch('audioPlayer/setSound', {
          sample: true
        });
        this.sampleAudioTrack.on('stop', () => {
          this.stopSampleAudio();
        });
      }
      this.$store.dispatch('audioPlayer/play');
    },

    // サンプル停止
    stopSampleAudio() {
      this.$store.dispatch('audioPlayer/pause');
      this.sampleAudioProgress = 0;
    },

    // Audio Player open (購入済み)
    openAudioPlayer(e) {
      if (window.open) {
        e.preventDefault();
        const windowSize = this.$store.getters['audioPlayer/windowSize'];
        window.open(e.currentTarget.href, null, `width=${windowSize.width},height=${windowSize.height},scrollbars=yes`);
      }
    },

    // プログレスバーのアップデート（パーセント）
    updateProgressSampleAudio(per) {
      if (!this.$refs.progressSlider) {
        return;
      }
      const progress = `${per}%`;
      this.$refs.progressSlider.style.background = `linear-gradient(to right, ${progressActiveColor} ${progress}, ${progressBaseColor} ${progress})`;
    },

    /*
    Events
    */
    // 複数SKU選択のスライドを開く
    onOpenSkuSelector() {
      return (this.state.isOpeningSkuSelector = true);
    },

    // 複数SKU選択のスライドを閉じる
    onCloseSkuSelector() {
      return (this.state.isOpeningSkuSelector = false);
    },

    // Favorites (Wish List) に追加/削除
    onToggleWishList() {
      if (!this.user.is_login) {
        window.location.href = `/login?r=${encodeURIComponent(
          '/shop/' + this.product._id
        )}&via=wishlist`;
        return;
      }
      // 変更を行う前に初期値を保存しておく
      if (!this.wishList.changedNow) {
        this.wishList.baseCount = this.selectedSku.wishlist_items_count;
        if (this.selectedSku.is_added_to_wishlist) {
          this.wishList.baseCount -= 1;
        }
      }

      return this.$store
        .dispatch('product/skus/toggleWishList', {
          product_id: this.product._id,
          sku_id: this.selectedSku._id,
          _csrf: this.user.csrf
        })
        .then(() => {
          const status = this.selectedSku.is_added_to_wishlist
            ? 'Added'
            : 'Removed';
          tom.helper.ga.push([
            'event',
            'WishList',
            status,
            this.selectedSku._id,
            1
          ]);
          return (this.wishList.changedNow = true);
        })
        .catch((err) => window.alert(err.message || 'An error occurred.'));
    },

    // 売り切れ商品の入荷通知 登録/登録解除
    onSubmitRegisterRestockNotification(register) {
      tom.helper.ga.push(['event', 'EC', 'WantIt', this.product._id, 1]);

      const action = register
        ? 'registerRestockNotification'
        : 'unregisterRestockNotification';
      this.restockNotification.loading = true;
      return this.$store
        .dispatch(`product/skus/${action}`, {
          product_id: this.product._id,
          sku_id: this.selectedSku._id,
          email: this.user.is_login ? '' : this.restockNotification.email,
          _csrf: this.user.csrf
        })
        .then(() => {
          return (this.restockNotification.loading = false);
        })
        .catch(() => {
          this.restockNotification.loading = false;
          return (this.restockNotification.errors = {
            message: 'An error occurred.'
          });
        });
    },

    onOpenTOMPremiumSummary(event) {
      if (this.isDesktop) {
        return this.$nextTick(() => {
          return this.onOpenPopoverTOMPremiumSummary(event);
        });
      } else {
        return this.onOpenModalTOMPremiumSummary();
      }
    },

    // What's TOM Premium がクリックされた（デスクトップ版）
    onOpenPopoverTOMPremiumSummary() {
      let $popover = $(this.$refs['popover-trigger']);
      if (!$popover.data('uiPopover')) {
        $popover = $popover.popover();
        return $popover.popover('show');
      }
    },

    // What's TOM Premium がクリックされた（モバイル版）
    onOpenModalTOMPremiumSummary() {
      return this.modalTOMPremiumSummary.open();
    },

    // TOM Premium への入会フラグが変更された
    onToggleJoinPremium(event, forceJoin) {
      return this.$store
        .dispatch('user/willJoinPremium', {
          join: forceJoin
        })
        .then(() => {
          // イベントが起きていない場合は意図的に変更しているためトラッキングを送信しない
          if (!event) {
            return;
          }
          const joinLabel = this.user.will_join_premium ? 'on' : 'off';
          const eventLabel = `premium-join_check-${joinLabel}`;
          tom.helper.ga.push([
            'event',
            'Premium',
            'Checked',
            eventLabel,
            1,
            true
          ]);
          return this.joinPremiumAsync();
        });
    },

    // Premium Summary内のAdd to Cart（モーダル・ポップオーバー共に）
    onAddToCartFromTomPremiumSummary(event) {
      return this.onToggleJoinPremium(event, true).then(() => {
        this.landingState.willJoinPremium = true;
        return this.onAddToCart(event);
      });
    },

    onAddToCartBuyNow(event) {
      return this.onAddToCart(event, '/checkout');
    },

    // Add to Cart
    onAddToCart(event, url) {
      const redirectUrl = url ? url : '/cart';
      if (this.status === statuses.LOADING) {
        return;
      }
      this.status = statuses.LOADING;
      this.loadedProductData = false;
      return Promise.resolve()
        .then(() => this.joinPremiumAsync())
        .then(() => this.addToCartAsync())
        .then(() => (window.location.href = redirectUrl))
        .catch((err) => {
          this.loadedProductData = true;
          if (err.type === 'overMaxPurchaseQty') {
            window.tom.helper.product.showModalOverMaxPurchaseQty();
            return;
          }

          return window.alert(err.message || 'An error occurred.');
        })
        .then(() => {
          return (this.status = statuses.LOADED);
        });
    },

    onCheckoutPointsRedemption() {
      this.status = statuses.LOADING;
      this.loadedProductData = false;
      pointsRedemptionApi
        .canCheckout({
          product_id: this.product._id,
          sku_id: this.selectedSku._id
        })
        .then((data) => {
          location.href = data.redirect;
        })
        .catch((err) => {
          if (
            err.message ===
            'You’ve reached the max order limit for this product.'
          ) {
            window.tom.helper.product.showModalOverMaxPurchaseQty({
              isPointsRedemption: true
            });
            return;
          }
          window.alert(err.message || 'An error occurred.');
        })
        .then(() => {
          this.loadedProductData = true;
          this.status = statuses.LOADED;
        });
    },

    // 非ログイン時の Buy Now ボタンの挙動
    onAddToCartBuyNowAuth() {
      if (this.status === statuses.LOADING) {
        return;
      }
      this.status = statuses.LOADING;
      this.loadedProductData = false;
      return Promise.resolve()
        .then(() => this.joinPremiumAsync())
        .then(() => this.addToCartAsync())
        .then(() => this.toAuth('signup'))
        .catch((err) => {
          this.loadedProductData = true;
          if (err.type === 'overMaxPurchaseQty') {
            window.tom.helper.product.showModalOverMaxPurchaseQty();
            return;
          }

          return window.alert(err.message || 'An error occurred.');
        })
        .then(() => {
          this.loadedProductData = true;
          this.status = statuses.LOADED;
          return;
        });
    },

    onCheckoutPointsRedemptionAuth() {
      window.options.redirect = `/checkout/points-redemption/${this.product._id}/${this.selectedSku._id}`;
      this.toAuth('signup');
    },

    onJoinPremium() {
      window.location.href = '/premium';
    },

    onJoinPremiumAuth() {
      window.options.redirect = '/premium';
      this.toAuth('signup');
    },

    // toAuth
    toAuth(type) {
      this.$store.commit('authType', type);
      return this.$modal.show('auth');
    },

    onRegistered(redirectUrl) {
      tom.helper.ga.push([
        'event',
        'Buy Now',
        'Checked',
        this.product._id,
        1,
        true
      ]);
      window.location.href = redirectUrl ? redirectUrl : '/cart';
    },

    closeAuth() {
      this.$store.commit('authType', null);
      this.removeOneFromCart();
    },

    // Premium 会員の入会フラグの ON/OFF を変更する非同期処理
    joinPremiumAsync() {
      if (this.user.is_premium_member && !this.user.can_buy_with_annual_plan) {
        return Promise.resolve();
      }
      return this.$store.dispatch('cart/joinPremium', {
        join: this.user.will_join_premium
      });
    },

    // Add to Cart 非同期処理
    addToCartAsync() {
      return this.$store.dispatch('cart/addToCart', {
        product_id: this.product._id,
        sku_id: this.selectedSku._id,
        ga: {
          action: 'Add to Cart',
          impression: {
            id: this.selectedSku._id,
            name: this.selectedSku.titles.en_us,
            category:
              this.product.category != null
                ? this.product.category.join('/')
                : undefined,
            price: this.getSellingPrice(
              this.selectedSku,
              this.$store.state.user
            ),
            quantity: 1
          }
        }
      });
    },

    // カートから追加されたSKUを１個削除
    removeOneFromCart() {
      this.status = statuses.LOADING;
      this.loadedProductData = false;
      return this.$store
        .dispatch('cart/fetch')
        .then(() =>
          this.$store.getters['cart/current']({populate: ['shipments']})
        )
        .then((cart) => {
          let shipmentId;
          let qty;
          for (const shipment of cart.shipments) {
            for (const item of shipment.items) {
              if (item.sku._id === this.selectedSku._id) {
                shipmentId = shipment._id;
                qty = item.qty;
                break;
              }
            }
          }
          return {shipmentId, qty};
        })
        .then(({shipmentId, qty}) =>
          this.$store.dispatch('cart/qty', {
            id: this.selectedSku._id,
            shipment_id: shipmentId,
            qty: qty - 1
          })
        )
        .catch((err) => {
          window.alert(err.message || 'An error occurred.');
        })
        .then(() => {
          this.loadedProductData = true;
          this.status = statuses.LOADED;
        });
    },

    // 獲得できるTOM Pointsに応じたクラスを返却する
    getPointClass(sku) {
      const per = ProductSku.getPointPercentage(sku, this.user);
      if (per >= 20) {
        return 'u-color--point-lv3';
      }
      if (per >= 10) {
        return 'u-color--point-lv2';
      }
      if (per > 1) {
        return 'u-color--point-lv1';
      }
      if (per <= 1) {
        return 'u-color--point-regular';
      }
    },

    // 残り在庫数に応じたクラスを返却する
    getClassSmallStock(sku) {
      const cnt = sku.stock_count;
      return {
        'c-label--danger': cnt <= 2,
        'c-label--warning': cnt > 2 && cnt <= 4,
        'c-label--info': cnt > 4 && cnt <= 6,
        'c-label--default': cnt > 6 && cnt <= 10
      };
    },

    canChangeStockCount(sku) {
      return (
        !sku.is_digital_content && !(sku.sales_method === this.SALES_METHOD.BTO)
      );
    },

    // Free Shipping になる SKU かどうか
    isFreeShipping(sku) {
      return (
        !sku.is_free_shipping_for_new_customer &&
        ((!this.user.will_join_premium && sku.is_free_shipping) ||
          (this.user.will_join_premium &&
            sku.is_free_shipping_for_premium_member)) &&
        !sku.is_digital_content
      );
    },

    // 初回購入者限定 Free Shipping になる SKU かどうか
    isFreeShippingForNewCustomer(sku) {
      return sku.is_free_shipping_for_new_customer && !sku.is_digital_content;
    },

    // 新規ユーザーもしくは Free Shipping SKU なら true
    // isFreeShipping と微妙に条件が違う
    isNewCustomerOrFreeShipping(sku) {
      if (this.isNewCustomer) {
        return true;
      } else {
        if (sku.is_free_shipping_for_new_customer || sku.is_digital_content) {
          return false;
        } else if (this.user.will_join_premium || this.user.is_premium_member) {
          return sku.is_free_shipping_for_premium_member;
        } else {
          return sku.is_free_shipping;
        }
      }
    },

    // 購入時に recontract_extra_point が貰える場合 true
    willGainRecontractExtraPoint(price) {
      return (
        this.user.can_gain_recontract_extra_point &&
        this.user.remain_subtotal_for_recontract_extra_point > 0 &&
        price > this.user.remain_subtotal_for_recontract_extra_point
      );
    },

    // 購入時に得られる recontract_extra_point による割引
    recontractExtraDiscount() {
      if ((this.user.recontract_extra_point || 0) > 0) {
        return bigdecimalUtil.divide(this.user.recontract_extra_point, 100);
      } else {
        return 0;
      }
    },

    onClickPic(event) {
      if (event.target.src.includes('product_no_image')) {
        return;
      }
      return this.$modal.show(event.target.alt);
    },
    closePic(event) {
      return this.$modal.hide(event.target.alt);
    },

    getFormatExpectedShipAt(expectedShipAt) {
      const year = expectedShipAt.getFullYear();
      const month = ('00' + (expectedShipAt.getMonth() + 1)).slice(-2);
      return year + '-' + month;
    },

    priceToObject(price, sku) {
      const symbol = sku.is_points_redemption
        ? '<i class="icon-tom-point"></i>'
        : '$';
      const decimal = '.';
      const priceArr = price.toFixed(2).split(decimal);
      return {
        surface: this.currency(price),
        symbol,
        decimal,
        min: {
          whole: Intl.NumberFormat().format(priceArr[0]).toString(),
          fraction: priceArr[1].toString()
        }
      };
    },

    /*
    Static (Helpers)
    */
    // SKU が Sale かどうか
    isSale: ProductSku.isSale,
    // SKU の Sale price と比較した割引率
    getDiscountRateFromSalePrice: ProductSku.getDiscountRateFromSalePrice,
    // SKU の Premium price と比較した割引率
    getDiscountRateFromPremiumPrice: ProductSku.getDiscountRateFromPremiumPrice,
    // SKU ストック数を表示するかどうか
    isDisplayStockCount: ProductSku.isDisplayStockCount,
    // SKU 売り切れ or 在庫数がないかどうか
    isOutOfStock: ProductSku.isOutOfStock,
    // SKU の定価よりも安く購入できる場合に表示する通常価格を取得する
    getRegularPrice: ProductSku.getRegularPrice,
    // SKU の実際に購入する販売金額を取得する
    getSellingPrice: ProductSku.getSellingPrice,
    getSellingPriceObject(sku, user) {
      return this.priceToObject(this.getSellingPrice(sku, user), sku);
    },
    // SKU の実際に購入する販売金額を取得する
    getDiscountRate: ProductSku.getDiscountRate,
    // SKU の獲得できるTOM Pointsを取得する
    getAcquirePoint: ProductSku.getAcquirePoint,
    // SKU の獲得できるTOM Pointsのキャッシュバック率を取得する
    getPointPercentage: ProductSku.getPointPercentage,
    // 配送予定日を `n week(s)`, `2 - 3 weeks`, `2 or 3 weeks` で返す
    getExpectedShipAtOrExtraWeek: ProductSku.getExpectedShipAtOrExtraWeek,
    isBto: ProductSku.isBto,
    isPreOrder: ProductSku.isPreOrder,
    getPremiumPrice: ProductSku.getPremiumPrice,
    getPremiumPriceObject(sku, user) {
      return this.priceToObject(this.getPremiumPrice(sku, user), sku);
    },
    getNonPremiumPrice: ProductSku.getNonPremiumPrice,
    getNonPremiumPriceObject(sku, user) {
      return this.priceToObject(this.getNonPremiumPrice(sku, user), sku);
    },
    getAnnualPremiumSkuPrice: ProductSku.getAnnualPremiumSkuPrice,
    getAnnualPremiumSkuPriceObject(sku, user) {
      return this.priceToObject(this.getAnnualPremiumSkuPrice(sku, user), sku);
    },
    getAnnualPremiumSkuDiscountdPrice:
    ProductSku.getAnnualPremiumSkuDiscountdPrice,
    getAnnualPremiumSkuDiscountdPriceObject(sku, user) {
      return this.priceToObject(
        this.getAnnualPremiumSkuDiscountdPrice(sku, user),
        sku
      );
    },
    displayPrice(value, sku) {
      let displayPrice = this.currency(value);
      if (sku.is_points_redemption) {
        displayPrice = displayPrice.replace(
          '$',
          '<i class="icon-tom-point"></i> '
        );
      } else {
        displayPrice = `US ${displayPrice}`;
      }
      return displayPrice;
    },
    getAnnualPremiumSkuPriceRate: ProductSku.getAnnualPremiumSkuPriceRate,
    getAnnualPremiumSkuDiscountRate: ProductSku.getAnnualPremiumSkuDiscountRate,
    hasEffectiveAnnualDiscount: ProductSku.hasEffectiveAnnualDiscount,
    // TOMバルク船便候補商品かつ在庫ある商品の場合
    isCanBulkShippingAndStock: ProductSku.isCanBulkShippingAndStock,
    isBeforeExpectedShipAt: ProductSku.isBeforeExpectedShipAt,
    // Utilities
    currency: textUtil.currency,
    resizedImageUrl: urlUtil.resizedImageUrl,
    webp: urlUtil.webp
  }
};
